import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { saleInstant } from '@util/storeSale/effectivePrice';

type SaleRecord = {
  sale_id: string;
  percent_off: number;
  starts_at: unknown;
  ends_at: unknown;
};

type SaleResponse = {
  current: SaleRecord | null;
  status: 'none' | 'scheduled' | 'active' | 'ended' | 'cancelled';
  eligible: boolean;
  counts?: { included: number; auction: number; own_markdown: number; unavailable: number } | null;
};

const STORE_SALE_FEE_PLACEHOLDER = 0.085;
export const STORE_SALE_COPY_C22A = 'Store sales are paused, so buyers see the regular price right now.';
function statusCopy(status: SaleResponse['status'], current: SaleRecord | null | undefined): string {
  if (status === 'none') return 'No store sale scheduled.';
  if (status === 'cancelled') return 'Cancelled';
  if (!current) return '';
  const end = formatCentral(saleInstant(current.ends_at) - 1000);
  if (status === 'scheduled') {
    return `Scheduled: ${current.percent_off}% off, ${formatCentral(current.starts_at)} through ${end} CT`;
  }
  if (status === 'active') return `Live: ${current.percent_off}% off through ${end} CT`;
  return `Ended ${end} CT`;
}

export function centralMidnight(date: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  const target = Date.UTC(year, month - 1, day);
  let instant = target + 6 * 60 * 60 * 1000;
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: 'numeric', day: 'numeric',
    hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23',
  });
  // Recalculate against Chicago wall time; this also handles the fall DST
  // change, when the offset at noon differs from the offset at midnight.
  for (let pass = 0; pass < 2; pass++) {
    const parts = Object.fromEntries(formatter.formatToParts(instant).map(({ type, value }) => [type, value]));
    const wallAsUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour), Number(parts.minute), Number(parts.second));
    instant += target - wallAsUtc;
  }
  return new Date(instant);
}

export function nextDate(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + 1)).toISOString().slice(0, 10);
}

function formatCentral(value: unknown): string {
  const instant = saleInstant(value);
  const date = Number.isFinite(instant) ? new Date(instant) : null;
  return date
    ? new Intl.DateTimeFormat('en-US', { timeZone: 'America/Chicago', dateStyle: 'medium', timeStyle: 'short' }).format(date)
    : 'Unknown time';
}

export default function StoreSaleSettings({ sellerId, renderPanel }: {
  sellerId: string;
  renderPanel: (panel: ReactElement) => ReactElement;
}) {
  const queryClient = useQueryClient();
  const [percentOff, setPercentOff] = useState(5);
  const [startNow, setStartNow] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const lastKnown = useRef<{ sellerId: string; data: SaleResponse } | null>(null);
  const pendingRequest = useRef<{ key: string; body: Record<string, unknown>; requestId: string } | null>(null);
  const saleQuery = useQuery<SaleResponse>({
    queryKey: ['store-sale-settings', sellerId],
    queryFn: async () => {
      const response = await fetch('/api/store-sale');
      if (!response.ok) throw new Error((await response.json()).error || 'Could not load store sale');
      return response.json();
    },
    enabled: !!sellerId && typeof fetch === 'function',
    refetchOnWindowFocus: true,
  });
  if (saleQuery.data) lastKnown.current = { sellerId, data: saleQuery.data };
  const saleData = saleQuery.data ?? (lastKnown.current?.sellerId === sellerId ? lastKnown.current.data : null);
  const current = saleData?.current;
  // A stale server status must not label a future sale as ended.
  const status = current && saleData?.status === 'ended' && Date.now() < saleInstant(current.starts_at)
    ? 'scheduled' : saleData?.status;
  const active = status === 'active' || status === 'scheduled';
  const sampleBuyerPays = Math.round(100000 * (100 - percentOff) / 100) / 100;
  const sampleSellerGets = Math.round(sampleBuyerPays * (1 - STORE_SALE_FEE_PLACEHOLDER) * 100) / 100;

  async function submit(path: string, body: Record<string, unknown>, attemptKey: string) {
    if (busy) return;
    if (pendingRequest.current?.key !== attemptKey) {
      pendingRequest.current = { key: attemptKey, body, requestId: crypto.randomUUID() };
    }
    const attempt = pendingRequest.current;
    setBusy(true);
    setMessage('');
    try {
      const response = await fetch(path, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...attempt.body, request_id: attempt.requestId }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'The sale could not be saved');
      pendingRequest.current = null;
      setMessage(path.endsWith('/cancel')
        ? 'Sale cancelled. Google ads may take several hours to show the regular price.'
        : 'Store sale scheduled.');
      await Promise.all([
        queryClient.invalidateQueries(['store-sale-settings', sellerId]),
        queryClient.invalidateQueries(['store-sales-active']),
      ]);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'The sale could not be saved');
    } finally {
      setBusy(false);
    }
  }

  const create = () => {
    if (!Number.isInteger(percentOff) || percentOff < 5 || percentOff > 50 || !endDate || (!startNow && !startDate)) {
      setMessage('Choose 5–50% off and a start and end date.');
      return;
    }
    const starts = startNow ? new Date() : centralMidnight(startDate);
    const ends = centralMidnight(nextDate(endDate));
    const duration = ends.getTime() - starts.getTime();
    if (!Number.isFinite(duration) || duration < 60 * 60 * 1000 || duration > 30 * 24 * 60 * 60 * 1000) {
      setMessage('The sale must last at least one hour and no more than 30 days.');
      return;
    }
    const attemptKey = JSON.stringify(['create', percentOff, startNow, startDate, endDate]);
    void submit('/api/store-sale', {
      percent_off: percentOff, starts_at: starts.toISOString(), ends_at: ends.toISOString(),
    }, attemptKey);
  };

  if (!saleData?.eligible && !current) return null;

  return renderPanel(
    <div
      className="w-full space-y-4 rounded-xl border border-gray-200 bg-white p-5 text-[1.4rem] text-brand-secondary"
      onKeyDown={(event) => {
        // This panel is inside the existing Shop Settings form. Enter in a
        // sale field must not submit unrelated profile/banner edits.
        if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
          event.preventDefault();
        }
      }}
    >
      {saleQuery.isLoading && <p>Loading store sale…</p>}
      {saleQuery.isError && <p role="alert">{saleQuery.error instanceof Error ? saleQuery.error.message : 'Could not load store sale'}</p>}
      {status && <p>{statusCopy(status, current)}</p>}
      {status === 'active' && saleData?.eligible === false && <p>{STORE_SALE_COPY_C22A}</p>}
      {saleData && !saleData.eligible && !active && (
        <p>Store sales are not available for your shop yet.</p>
      )}
      {saleData?.counts && (
        <p>{saleData.counts.included} listings included. Excluded: {saleData.counts.auction} auctions, {saleData.counts.own_markdown} listings already marked down, {saleData.counts.unavailable} unavailable listings.</p>
      )}
      {!active && saleData?.eligible && (
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="flex flex-col gap-1">Percent off (5–50)
            <input type="number" min={5} max={50} step={1} value={percentOff}
              onChange={(event) => setPercentOff(Number(event.target.value))}
              className="rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <label className="flex items-center gap-2 self-end">
            <input type="checkbox" checked={startNow} onChange={(event) => setStartNow(event.target.checked)} />
            Start now
          </label>
          {!startNow && <label className="flex flex-col gap-1">Start date (Central)
            <input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2" />
          </label>}
          <label className="flex flex-col gap-1">Last sale day (Central)
            <input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2" />
          </label>
          <p className="sm:col-span-2">Example: a $1,000 item costs the buyer ${sampleBuyerPays.toFixed(2)}; the seller receives about ${sampleSellerGets.toFixed(2)} before other deductions.</p>
          <button type="button" disabled={busy || saleQuery.isLoading} onClick={create}
            className="rounded-lg bg-[#C75008] px-4 py-2 font-semibold text-white disabled:opacity-50">
            Schedule store sale
          </button>
        </div>
      )}
      {active && current && <button type="button" disabled={busy}
        onClick={() => void submit('/api/store-sale/cancel', { sale_id: current.sale_id }, `cancel:${current.sale_id}`)}
        className="rounded-lg border border-gray-300 px-4 py-2 font-semibold disabled:opacity-50">
        Cancel sale
      </button>}
      {message && <p role="status">{message}</p>}
      <p className="text-[1.2rem] text-gray-600">Auctions and listings with their own markdown are excluded. Offers remain available where enabled. Cancelling early can take several hours to clear from Google ads.</p>
    </div>
  );
}
